<template>
  <PageChrome :show-header="false">
    <div class="acctDetail" v-if="account">
      <!-- Header -->
      <header class="acctHeader">
        <div class="acctIdentity">
          <span class="acctLogo">
            <FavIcon :domain="account.domain" :size="40" />
          </span>
          <div class="acctText">
            <h1 class="acctName">{{ account.name || account.domain }}</h1>
            <div class="acctMeta">
              <a
                :href="`https://${account.domain}`"
                target="_blank"
                rel="noopener"
                class="acctDomain"
              >{{ account.domain }} &#x2197;</a>
              <span v-if="hasSalesforce || hasHubspot || hasPipedrive || hasLeadIQ" class="acctBadges">
                <a
                  v-if="hasSalesforce"
                  :href="salesforceUrl || undefined"
                  target="_blank"
                  rel="noopener"
                  class="badgeLink"
                  title="Open in Salesforce"
                ><AppIcon name="salesforce" :size="16" /></a>
                <a
                  v-if="hasHubspot"
                  :href="hubspotUrl || undefined"
                  target="_blank"
                  rel="noopener"
                  class="badgeLink"
                  title="Open in HubSpot"
                ><AppIcon name="hubspot" :size="16" /></a>
                <a
                  v-if="hasPipedrive"
                  :href="pipedriveUrl || undefined"
                  target="_blank"
                  rel="noopener"
                  class="badgeLink"
                  title="Open in Pipedrive"
                ><AppIcon name="pipedrive" :size="16" /></a>
                <span v-if="hasLeadIQ" class="badgeIcon" title="Enriched by LeadIQ">
                  <AppIcon name="leadiq" :size="16" />
                </span>
              </span>
            </div>
          </div>
        </div>
        <span class="crmSync">
          <span v-if="crmRefreshing" class="crmSpinner" />
          <span v-else-if="crmSyncLabel" class="crmSyncLabel">{{ crmSyncLabel }}</span>
        </span>
      </header>

      <!-- Kanban board -->
      <section class="kanbanSection">
        <h2 class="sectionTitle">Buying Team</h2>
        <div v-if="loading" class="muted">Loading buying team...</div>
        <div v-else-if="allMembers.length === 0" class="muted">No buying team members yet.</div>
        <div v-else class="kanban">
          <div
            v-for="col in kanbanColumns"
            :key="col.key"
            class="kanbanCol"
          >
            <div class="kanbanColHeader">
              <span class="kanbanColTitle">{{ col.label }}</span>
              <span class="kanbanColCount">{{ col.members.length }}</span>
            </div>
            <div class="kanbanColBody">
              <RouterLink
                v-for="member in col.members"
                :key="member.id"
                :to="memberDetailHref(member)"
                class="kanbanCard"
              >
                <div class="kanbanCardTop">
                  <span class="kanbanCardDot" :style="{ borderColor: sentimentColor(member.engagement_sentiment) }">
                    {{ memberInitials(member) }}
                  </span>
                  <div class="kanbanCardIdentity">
                    <span class="kanbanCardName">{{ memberName(member) }}</span>
                    <span class="kanbanCardTitle">{{ memberJobTitle(member) }}</span>
                  </div>
                </div>
                <div v-if="memberPlays(member).length" class="kanbanCardPlays">
                  <span
                    v-for="play in memberPlays(member)"
                    :key="play"
                    class="kanbanPlayPill"
                  >{{ play }}</span>
                </div>
              </RouterLink>
              <div v-if="col.members.length === 0" class="kanbanEmpty">No contacts</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Activity Timeline -->
      <section class="timelineSection">
        <h2 class="sectionTitle">Activity Timeline</h2>
        <ActivityTimeline :account-id="accountId" />
      </section>
    </div>
    <div v-else-if="loadError" class="muted loadError">Account not found.</div>
    <div v-else class="muted loadError">Loading...</div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import FavIcon from '../../components/FavIcon.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { getSalesforceUrl } from '../../services/salesforce'
import { getHubspotUrl, getPipedriveUrl, preloadCrmLinkConfig } from '../../services/crmLinks'
import ActivityTimeline from '../components/ActivityTimeline.vue'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_ACCOUNT } from '../../drawer/drawerTypes'
import { useAccountBasics } from '../../hooks/useAccountBasics'

type BuyingTeamMember = {
  id: string
  contact_id?: string | null
  display?: Record<string, any>
  role?: string
  lifecycle_state: string
  persona_key: string
  persona_name: string
  seniority_level?: string
  engagement_sentiment?: string
  engagement_score?: number
}

type PlayMembership = {
  membership_id: string
  sales_play_name: string
  state: string
}

const LIFECYCLE_COLUMNS = [
  { key: 'none', label: 'New' },
  { key: 'staged', label: 'Staged' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'outbound', label: 'Outbound' },
  { key: 'engaged', label: 'Engaged' },
  { key: 'nurture', label: 'Nurture' },
  { key: 'customer', label: 'Customer' },
  { key: 'disqualified', label: 'Disqualified' },
  { key: 'no_longer_at_company', label: 'No Longer at Company' },
  { key: 'outbound_in_other_system', label: 'Outbound (Other)' },
]

const route = useRoute()
const { setContext, openDrawer } = useContextDrawer()

const allMembers = ref<BuyingTeamMember[]>([])
const memberPlayMap = ref<Map<string, PlayMembership[]>>(new Map())
const loading = ref(false)
const loadError = ref(false)

const accountId = computed(() => String(route.params.id || '').trim())
const { basics: account } = useAccountBasics(accountId)

const hasSalesforce = computed(() => !!account.value?.salesforce_account_id)
const salesforceUrl = computed(() =>
  account.value?.salesforce_account_id
    ? getSalesforceUrl({ Id: account.value.salesforce_account_id }, 'Account')
    : null
)
const crmReady = ref(false)

const hasHubspot = computed(() => !!account.value?.hubspot_company_id)
const hubspotUrl = computed(() => {
  void crmReady.value
  return account.value?.hubspot_company_id
    ? getHubspotUrl('company', account.value.hubspot_company_id)
    : null
})
const hasPipedrive = computed(() => !!account.value?.pipedrive_org_id)
const pipedriveUrl = computed(() => {
  void crmReady.value
  return account.value?.pipedrive_org_id
    ? getPipedriveUrl('organization', account.value.pipedrive_org_id)
    : null
})
const hasLeadIQ = computed(() => account.value?.leadiq?.enrichment_status === 'succeeded')

const kanbanColumns = computed(() => {
  const membersByState = new Map<string, BuyingTeamMember[]>()
  for (const m of allMembers.value) {
    const state = m.lifecycle_state || 'none'
    if (!membersByState.has(state)) membersByState.set(state, [])
    membersByState.get(state)!.push(m)
  }
  // Only show columns that have members, plus always show the first few core columns
  const coreKeys = new Set(['none', 'outbound', 'engaged', 'nurture'])
  return LIFECYCLE_COLUMNS.filter(
    col => coreKeys.has(col.key) || (membersByState.get(col.key)?.length ?? 0) > 0
  ).map(col => ({
    ...col,
    members: membersByState.get(col.key) || [],
  }))
})

function sentimentColor(sentiment: string | undefined) {
  const s = String(sentiment || 'none').trim().toLowerCase()
  if (s === 'positive') return '#16a34a'
  if (s === 'negative') return '#dc2626'
  if (s === 'mixed') return '#2563eb'
  return '#cbd5e1'
}

function memberInitials(member: BuyingTeamMember) {
  const source = String(member?.display?.name || member?.display?.email || '').trim()
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase()
}

function memberName(member: BuyingTeamMember) {
  return String(member?.display?.name || member?.display?.email || 'Unknown').trim()
}

function memberJobTitle(member: BuyingTeamMember) {
  return String(member?.display?.title || member?.display?.job_title || member?.role || '').trim()
}

function memberDetailHref(member: BuyingTeamMember) {
  if (member.contact_id) {
    return `/contacts/${encodeURIComponent(member.contact_id)}`
  }
  return '#'
}

function memberPlays(member: BuyingTeamMember): string[] {
  const plays = memberPlayMap.value.get(String(member.contact_id || ''))
  if (!plays) return []
  return plays
    .filter(p => p.state === 'qualified' || p.state === 'active' || p.state === 'staged')
    .map(p => p.sales_play_name)
}


// ── CRM sync status ────────────────────────────────────────────
const crmRefreshing = ref(false)
const crmSyncedAt = ref<number | null>(null)
const tick = ref(0)
let tickTimer: ReturnType<typeof setInterval> | null = null

const crmSyncLabel = computed(() => {
  void tick.value // reactive dependency for periodic re-evaluation
  const t = crmSyncedAt.value
  if (!t) return ''
  const secs = Math.round((Date.now() - t) / 1000)
  if (secs < 10) return 'Synced just now'
  if (secs < 60) return `Synced ${secs}s ago`
  const mins = Math.round(secs / 60)
  if (mins < 60) return `Synced ${mins}m ago`
  return `Synced ${Math.round(mins / 60)}h ago`
})

function refreshCrms() {
  const id = accountId.value
  if (!id) return
  crmRefreshing.value = true
  const opts = { method: 'POST' } as const
  Promise.allSettled([
    apiFetch(`/accounts/${id}/refresh_salesforce?force=false`, opts),
    apiFetch(`/accounts/${id}/refresh_pipedrive?force=false`, opts),
    apiFetch(`/accounts/${id}/refresh_hubspot?force=false`, opts),
  ]).then(() => {
    crmRefreshing.value = false
    crmSyncedAt.value = Date.now()
  })
}

async function loadBuyingTeam() {
  const id = accountId.value
  if (!id) { allMembers.value = []; return }
  loading.value = true
  try {
    const resp = await apiFetch(`/accounts/${id}/buying_team`)
    const plays = Array.isArray(resp?.plays) ? resp.plays : []
    // Flatten members across all play views, deduplicating by id
    const seen = new Set<string>()
    const flat: any[] = []
    for (const play of plays) {
      for (const m of (play.members || [])) {
        const mid = String(m.id || '')
        if (mid && !seen.has(mid)) { seen.add(mid); flat.push(m) }
      }
    }
    allMembers.value = flat

    // Load lifecycle card for each member with a contact_id to get their play memberships
    const playMap = new Map<string, PlayMembership[]>()
    const contactIds = allMembers.value
      .map(m => String(m.contact_id || '').trim())
      .filter(Boolean)
    const uniqueContactIds = [...new Set(contactIds)]

    await Promise.all(
      uniqueContactIds.map(async (cid) => {
        try {
          const lc = await apiFetch(`/contacts/${cid}/lifecycle-card`)
          if (Array.isArray(lc?.sales_play_memberships)) {
            playMap.set(cid, lc.sales_play_memberships)
          }
        } catch { /* ignore individual failures */ }
      })
    )
    memberPlayMap.value = playMap
  } catch {
    allMembers.value = []
  } finally {
    loading.value = false
  }
}

function setDrawerContext() {
  const id = accountId.value
  if (!id) return
  setContext({ type: DRAWER_CONTEXT_ACCOUNT, accountId: id })
  openDrawer()
}

watch(accountId, () => {
  void loadBuyingTeam()
  setDrawerContext()
  refreshCrms()
})

onMounted(async () => {
  void loadBuyingTeam()
  setDrawerContext()
  refreshCrms()
  tickTimer = setInterval(() => { tick.value++ }, 30_000)
  await preloadCrmLinkConfig()
  crmReady.value = true
})

onUnmounted(() => {
  if (tickTimer) clearInterval(tickTimer)
})
</script>

<style scoped>
.acctDetail {
  display: flex;
  flex-direction: column;
  gap: var(--space-6, 24px);
}

/* ── Header ──────────────────────────────────────────────────────── */
.acctHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4, 16px);
}

.acctIdentity {
  display: flex;
  align-items: center;
  gap: var(--space-4, 16px);
  min-width: 0;
}

.acctLogo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg, 12px);
  background: var(--color-bg-surface, #fff);
  box-shadow: var(--shadow-neu-out-cream, 0 2px 8px rgba(0,0,0,.08));
  flex-shrink: 0;
  overflow: hidden;
}

.acctText {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.acctName {
  font-family: var(--font-sans);
  font-size: var(--fs-xl, 20px);
  font-weight: var(--fw-bold, 700);
  color: var(--color-text-primary, #1a1a1a);
  letter-spacing: var(--tracking-tight, -0.01em);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acctMeta {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
}

.acctDomain {
  font-family: var(--font-sans);
  font-size: var(--fs-sm, 14px);
  color: var(--color-text-tertiary, #737373);
  text-decoration: none;
  transition: color 150ms ease;
}
.acctDomain:hover { color: var(--color-accent, #059669); }

.acctBadges {
  display: flex;
  align-items: center;
  gap: 4px;
}

.badgeLink {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.badgeIcon {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

/* ── CRM sync indicator ─────────────────────────────────────────── */
.crmSync {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.crmSpinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--color-border, #e5e2dc);
  border-top-color: var(--color-accent, #059669);
  border-radius: 50%;
  display: inline-block;
  animation: crm-spin 0.8s linear infinite;
}

@keyframes crm-spin {
  to { transform: rotate(360deg); }
}

.crmSyncLabel {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  white-space: nowrap;
}

/* ── Kanban ──────────────────────────────────────────────────────── */
.sectionTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-md, 16px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-text-primary, #1a1a1a);
  margin: 0 0 var(--space-3, 12px);
}

.kanban {
  display: flex;
  gap: var(--space-3, 12px);
  overflow-x: auto;
  padding-bottom: var(--space-3, 12px);
  min-height: 200px;
}

.kanbanCol {
  flex: 0 0 220px;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface, #faf9f6);
  border-radius: var(--radius-lg, 12px);
  border: 1px solid var(--color-border, #e5e2dc);
  overflow: hidden;
}

.kanbanColHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--color-border, #e5e2dc);
  background: var(--color-bg-panel, #f5f3ee);
}

.kanbanColTitle {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-secondary, #525252);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kanbanColCount {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-tertiary, #a3a3a3);
  background: var(--color-bg-app, #eae7df);
  border-radius: var(--radius-full, 9999px);
  padding: 1px 7px;
  min-width: 20px;
  text-align: center;
}

.kanbanColBody {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  overflow-y: auto;
  flex: 1;
}

.kanbanCard {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-app, #fff);
  border: 1px solid var(--color-border, #e5e2dc);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 150ms ease, border-color 150ms ease;
}
.kanbanCard:hover {
  box-shadow: var(--shadow-neu-out-cream-sm, 0 2px 6px rgba(0,0,0,.06));
  border-color: var(--color-accent, #059669);
}

.kanbanCardTop {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kanbanCardDot {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 2px solid var(--color-border);
  background: var(--color-bg-surface, #fff);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  flex-shrink: 0;
  color: var(--color-text-secondary);
}

.kanbanCardIdentity {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.kanbanCardName {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary, #1a1a1a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanbanCardTitle {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanbanCardPlays {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.kanbanPlayPill {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  background: var(--color-info-soft, #eff6ff);
  color: var(--color-info, #1d4ed8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.kanbanEmpty {
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
  padding: 8px 4px;
  text-align: center;
}

.muted {
  font-size: 13px;
  color: var(--color-text-tertiary, #a3a3a3);
}

.loadError {
  padding: var(--space-5, 20px);
}

.timelineSection {
  margin-top: var(--space-2, 8px);
}
</style>
